import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Lock } from "lucide-react";
import MainFeed from "../components/Feed/MainFeed";
import ProfileLeftSidebar from "../components/Profile/LeftSidebar";
import RightSidebar from "../components/Feed/RightSidebar";
import { useProfileByPostId } from "../hooks/profileQueries";
import { useMediaQuery } from "../hooks/useMediaQuery";
import NotFound from "../pages/NotFound";
import WryvenLoader from '../components/Loaders/WryvenLoader'

const PrivacyLock = ({ username }) => (
  <div className="min-h-[400px] flex items-center justify-center">
    <div className="flex flex-col items-center gap-2 max-w-md text-center">
      <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full border-2 border-black flex items-center justify-center">
        <Lock className="w-10 h-10 sm:w-12 sm:h-12 text-black stroke-[1.5]" />
      </div>
      <h2 className="text-xl sm:text-2xl font-bold text-black">This Account is Private</h2>
      <p className="text-black text-sm sm:text-base leading-relaxed">
        Only approved followers can see {username}’s posts and stories.
      </p>
    </div>
  </div>
);

const PostDetailPage = () => {
  const { postId } = useParams(); 
  const navigate = useNavigate();
  const location = useLocation();

  const isDesktop = useMediaQuery('(min-width: 1280px)');

  const searchParams = new URLSearchParams(location.search);
  const postType = searchParams.get("type") || location.state?.postType || "all";
  const { data: userData, isLoading } = useProfileByPostId(postId);

  const username = userData?.username;

  const [canViewPosts, setCanViewPosts] = useState(
    userData?.isOwnProfile || userData?.privacySettings?.isPrivateAccount === 0
  );

  useEffect(() => {
    const isPublic = userData?.privacySettings?.isPrivateAccount === 0;
    const isFollowingAndAccepted = userData?.followStatus?.isFollowing && userData?.followStatus?.isAccepted === 1;
    const canView = userData?.isOwnProfile || isPublic || isFollowingAndAccepted;
    setCanViewPosts(canView);
  }, [userData]);

  const handleClose = () => {
    if (postType && postType !== "all") {
      navigate(`/${username}?tab=${postType}`);
    } else {
      navigate(`/${username}`);
    }
  };

  if (isLoading) {
    return <WryvenLoader />;
  }

  if (!userData?.profileID) {
    return <NotFound />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation Bar */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-white">
        <div className="max-w-7xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {/* Custom SVG Back Button */}
            <button
              onClick={handleClose}
              className="w-8 h-8 flex items-center justify-center text-black"
              aria-label="Go back"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="black"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="19" y1="12" x2="5" y2="12" />
                <polyline points="12 19 5 12 12 5" />
              </svg>
            </button>

            {/* Dynamic Type Text - Black and Semibold */}
            <h1 className="text-black font-semibold text-lg capitalize">
              {postType === "all" ? "Posts" : postType}
            </h1>
          </div>
        </div>
      </div>

      {/* Main Layout */}
      <div className="pt-10 sm:pt-20 flex justify-center max-w-7xl mx-auto gap-4 xl:gap-8 px-4 lg:px-6">
        
        {/* Left Sidebar */}
        <div className="hidden lg:block flex-shrink-0 w-[335px] max-w-[335px]">
          <ProfileLeftSidebar userData={userData} />
        </div>

        {/* Main Feed Content */}
        <div className="flex-1 min-w-0 max-w-xl mt-4">
          {!canViewPosts ? (
            <PrivacyLock username={userData?.username} />
          ) : (
            <MainFeed
              isDetailView={true}
              username={username}
              postId={postId}
              profileID={userData.profileID}
              postType={postType}
            />
          )}
        </div>

        {/* Right Sidebar */}
        {isDesktop && (
          <div className="flex-shrink-0 w-80 mt-4">
            <RightSidebar isVisible={isDesktop} />
          </div>
        )}
      </div>
    </div>
  );
};

export default PostDetailPage;